import './QuestionDetails.scss';

import React, { useState } from 'react';

import { SlDetails } from '@shoelace-style/shoelace/dist/react';
import { QuestionTypes } from '@showbie-socrative/socrative-utils';
import cx from 'classnames';

import { translate } from '~l10n/client';
import { IconChevron } from '~shared/components/icons/standard';
import ChoiceLabel from '~teacher/components/QuestionMultipleChoice/ChoiceLabels';
import { InfoIcon } from '~teacher/components/QuizQuestion/InfoIcon';

import { QuestionResponse } from '../../../../api/ai';

const questionTypeLabels = new Map<QuestionTypes, string>([
  [QuestionTypes.TrueFalse, translate('True / False')],
  [QuestionTypes.MultipleChoice, translate('Multiple Choice')],
  [QuestionTypes.FreeResponse, translate('Short Answer')],
]);

interface QuestionDetailsProps {
  questionType: QuestionTypes;
  questionText: string;
  answers?: Array<{
    order: number;
    text: string;
    isCorrect: boolean;
  }>;
  explanation?: string;
}
export function QuestionDetails({
  questionType,
  questionText,
  answers = [],
  explanation,
}: QuestionDetailsProps) {
  const isSAQuestion = questionType === QuestionTypes.FreeResponse;
  const [isOpen, setIsOpen] = useState(false);
  return (
    <SlDetails
      className="question-details"
      onSlShow={() => setIsOpen(true)}
      onSlHide={() => setIsOpen(false)}
      disabled={isSAQuestion}
    >
      <div slot="summary" className="flex justify-between w-full gap-4">
        <div className="flex flex-col gap-2">
          <p className="text-xs font-600 text-grey-900">
            {questionTypeLabels.get(questionType)}
          </p>
          <p className="text-base leading-8 text-blue-900">{questionText}</p>
        </div>
        {isSAQuestion ? null : (
          <div className="flex gap-4 text-grey-700">
            <IconChevron direction={isOpen ? 'up' : 'down'} />
          </div>
        )}
      </div>
      <div className="question-details">
        <QuestionDetailsAnswers questionType={questionType} answers={answers} />
        {explanation ? (
          <QuestionsDetailsExplanation>
            {explanation}
          </QuestionsDetailsExplanation>
        ) : null}
      </div>
    </SlDetails>
  );
}

interface QuestionAnswersProps {
  questionType: QuestionTypes;
  answers: QuestionResponse['answers'];
}
function QuestionDetailsAnswers({
  questionType,
  answers,
}: QuestionAnswersProps) {
  if (questionType === QuestionTypes.TrueFalse) {
    const correctAnswer =
      answers.find((answer) => answer.isCorrect)?.text === 'True';
    return (
      <div
        className={cx(
          'p-3 mb-6 rounded-1 font-sans text-base font-600 leading-base text-white text-center w-fit',
          {
            'bg-green-500': correctAnswer === true,
            'bg-red-500': correctAnswer === false,
          }
        )}
      >
        {correctAnswer === true ? translate('True') : translate('False')}
      </div>
    );
  }

  if (questionType === QuestionTypes.FreeResponse) {
    return (
      <div className="max-w-md flex flex-wrap mb-6 max-w-5xl sm:pr-12 p-0 -mt-4">
        {answers.map((answer) => (
          <span
            className="px-4 py-3 mr-4 mt-4 rounded-1 text-display inline-block text-green-600 bg-green-50"
            key={answer.order}
          >
            {answer.text}
          </span>
        ))}
      </div>
    );
  }

  if (questionType === QuestionTypes.MultipleChoice) {
    return (
      <div className="quiz-question mb-6">
        {answers.map((answer) => (
          <div className="flex gap-2" key={answer.order}>
            <div className="relative flex items-center justify-center w-12 h-12">
              <ChoiceLabel isCorrect={answer.isCorrect} />
              {answer.isCorrect ? <CorrectAnswerIcon /> : null}
            </div>
            <p className="max-w-lg text-base leading-8 pt-2 text-blue-900">
              {answer.text}
            </p>
          </div>
        ))}
      </div>
    );
  }

  return <div>{questionType} is an unsupported question type.</div>;
}

interface QuestionsDetailsExplanationProps {
  children: React.ReactNode;
}
function QuestionsDetailsExplanation({
  children,
}: QuestionsDetailsExplanationProps) {
  return (
    <div className="flex gap-2">
      <div className="px-2">
        <InfoIcon hasOutline a11yLabel={translate('Explanation (Optional)')} />
      </div>
      <p className="max-w-lg text-base leading-8 text-blue-900">{children}</p>
    </div>
  );
}

const CorrectAnswerIcon = () => (
  <div className="correct-answer-icon">
    <svg
      width={18}
      height={18}
      viewBox="0 0 18 18"
      fill="none"
      aria-describedby="correct-answer-title"
      xmlns="http://www.w3.org/2000/svg"
    >
      <title id="correct-answer-title">{translate('Correct')} </title>
      <rect width={18} height={18} rx={9} className="fill-green-50" />
      <path
        d="M4 8.21053L7.87097 12L14 6"
        className="stroke-green-800"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </div>
);
